/**
 * GET HELP — writing to us rather than to a door (wave 14a).
 *
 * Everything else in this product is a knock: a stranger at somebody's door,
 * assessed, gated, metered. This is the one message that is none of those. It
 * costs nothing, it is not scored, no doorman reads it, and the person at the
 * other end is a person.
 *
 * THE REPLY LEAVES THE APP, and the screen says so in the first line rather
 * than in a tooltip. The answer goes to the verified contact address — the same
 * address the Morning Nob goes to — because that is where somebody actually
 * reads their mail, and because a second copy of the correspondence inside the
 * app would be a second place for a thread to be half right. The list below is
 * therefore subjects and states, never words.
 *
 * THE SECOND ROAD IS ALWAYS NAMED. `support@nob.me` is the desk's own inbox and
 * it reaches the same people whether or not this form works at all — which is
 * why the "no desk" state here is a sentence pointing at the address, not an
 * apology.
 */
import { useCallback, useId, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { SUPPORT_BODY_MAX, SUPPORT_SUBJECT_MAX } from "@nob/schemas";
import { openSupportThread } from "../../lib/api";
import { qk, supportQuery } from "../../lib/queries";
import { SecondaryButton } from "../ui/button";
import { Group, PLATE } from "../shell/surfaces";

export const HELP_LABEL = "Get help";
export const HELP_LEAD = "A person reads these. It is not a knock, and it costs nothing.";
export const HELP_SUBJECT_LABEL = "Subject";
export const HELP_BODY_LABEL = "What is going on?";
export const HELP_SEND_LABEL = "Send";
export const HELP_SENT_ACK = "Sent. Nob will write back.";
export const HELP_NO_DESK =
  "The help desk is not reachable from this build. Write to support@nob.me instead.";
export const HELP_TOO_MANY =
  "That is as many as Nob takes in a day. Reply to one of the messages you have already sent.";
export const HELP_SIMULATED =
  "Simulated desk on this build: nothing you send here leaves this machine.";
export const HELP_UNREACHABLE = "Nob is not answering. Try that again.";
export const HELP_EMPTY_LIST = "You have not written to us yet.";
export const HELP_NO_ADDRESS =
  "Nob has no proved address for you yet, so there is nowhere to reply to. Add one under Account first.";

/** Where the answer lands, said in the words the person needs to check it. */
export function repliesLine(address: string | null, supportAddress: string): string {
  return address === null
    ? `Replies go to the address on your account. You can also write to ${supportAddress}.`
    : `Replies go to ${address}. You can also write to ${supportAddress}.`;
}

const FIELD =
  "w-full rounded-lg border border-border bg-bg/60 px-3 py-2 text-sm text-text " +
  "placeholder:text-fainter focus-visible:border-border-loud focus-visible:outline-none " +
  "focus-visible:ring-2 focus-visible:ring-focus/40 disabled:opacity-50 " +
  "aria-[invalid=true]:border-danger/60";

function when(iso: string | null): string {
  if (!iso) return "";
  const at = new Date(iso);
  return Number.isNaN(at.getTime()) ? "" : at.toLocaleDateString();
}

export function HelpPage() {
  const queryClient = useQueryClient();
  const headingId = useId();
  const { data } = useQuery(supportQuery());

  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [issue, setIssue] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  // THE FORM IS OFFERED ONLY WHEN A MESSAGE WOULD ARRIVE. `ready` is the api's
  // answer to exactly that question — on a deployed tier a mocked desk is not
  // ready (the mistake R-0908-3 deleted a screen over), on a laptop the
  // stand-in is, and `simulated` says so under the form.
  const reachable = data !== undefined && data.desk.ready;

  const send = useCallback(() => {
    const s = subject.trim();
    const b = body.trim();
    if (!s || !b || busy) return;
    setBusy(true);
    setIssue(null);
    void openSupportThread({ subject: s, body: b }).then((r) => {
      setBusy(false);
      if (!r.ok) {
        setIssue(
          r.code === "RATE_LIMITED"
            ? HELP_TOO_MANY
            : r.code === "NO_DESK"
              ? HELP_NO_DESK
              : HELP_UNREACHABLE,
        );
        return;
      }
      setSubject("");
      setBody("");
      setSent(true);
      // The server's copy is what the list reads — the row it just wrote is
      // already there, so nothing here has to invent one.
      void queryClient.invalidateQueries({ queryKey: qk.support() });
    });
  }, [body, busy, queryClient, subject]);

  return (
    <>
      <Group title={HELP_LABEL} titleId={headingId}>
        <div className={PLATE}>
          <p className="text-[12px] leading-snug text-faint">{HELP_LEAD}</p>
          <p className="mt-1.5 text-[12px] leading-snug text-faint">
            {repliesLine(data?.repliesTo ?? null, data?.supportAddress ?? "support@nob.me")}
          </p>
          {data !== undefined && data.repliesTo === null && (
            <p className="mt-1.5 text-[12px] leading-snug text-faint">{HELP_NO_ADDRESS}</p>
          )}

          {reachable ? (
            <form
              className="mt-3"
              noValidate
              onSubmit={(e) => {
                e.preventDefault();
                send();
              }}
            >
              <label
                className="block text-[11px] uppercase tracking-[0.12em] text-faint"
                htmlFor="help-subject"
              >
                {HELP_SUBJECT_LABEL}
              </label>
              <input
                id="help-subject"
                value={subject}
                disabled={busy}
                maxLength={SUPPORT_SUBJECT_MAX}
                aria-invalid={issue !== null}
                onChange={(e) => setSubject(e.target.value)}
                className={`${FIELD} mt-1.5`}
              />

              <label
                className="mt-3 block text-[11px] uppercase tracking-[0.12em] text-faint"
                htmlFor="help-body"
              >
                {HELP_BODY_LABEL}
              </label>
              <textarea
                id="help-body"
                rows={5}
                value={body}
                disabled={busy}
                maxLength={SUPPORT_BODY_MAX}
                aria-invalid={issue !== null}
                onChange={(e) => setBody(e.target.value)}
                className={`${FIELD} mt-1.5 resize-y`}
              />

              {data?.desk.simulated && (
                <p className="mt-1.5 text-[12px] leading-snug text-faint">{HELP_SIMULATED}</p>
              )}
              {issue && (
                <p role="alert" className="mt-1.5 text-[13px] text-danger">
                  {issue}
                </p>
              )}

              <div className="mt-3 flex flex-wrap items-center gap-3">
                <SecondaryButton
                  type="submit"
                  disabled={busy || subject.trim() === "" || body.trim() === ""}
                >
                  {HELP_SEND_LABEL}
                </SecondaryButton>
                {sent && (
                  <span role="status" className="font-mono text-[11px] text-accent-ink/80">
                    {HELP_SENT_ACK}
                  </span>
                )}
              </div>
            </form>
          ) : (
            data !== undefined && (
              <p className="mt-3 text-[13px] leading-snug text-faint">{HELP_NO_DESK}</p>
            )
          )}
        </div>
      </Group>

      <Group title="What you have sent">
        <div className={PLATE}>
          {data === undefined ? (
            <p className="text-[12px] text-faint" aria-hidden />
          ) : data.threads.length === 0 ? (
            <p className="text-[12px] leading-snug text-faint">{HELP_EMPTY_LIST}</p>
          ) : (
            <ul className="space-y-2">
              {data.threads.map((t) => (
                <li key={t.id} className="text-[13px] leading-snug">
                  <span className="text-text">{t.subject ?? "(no subject)"}</span>
                  <span className="ml-2 text-[11px] uppercase tracking-[0.12em] text-faint">
                    {t.status}
                  </span>
                  <span className="ml-2 text-[11px] text-fainter">
                    {when(t.lastMessageAt ?? t.openedAt)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </Group>
    </>
  );
}
